import type { CSSProperties } from "react";

const FALLBACK_GRADIENT = {
  from: "#7c5d9e",
  to: "#9b7bb8",
};

const BLACK = { r: 0, g: 0, b: 0 };
const WHITE = { r: 255, g: 255, b: 255 };

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return null;
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  };
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[r, g, b]
    .map((x) => clamp(x).toString(16).padStart(2, "0"))
    .join("")}`;
}

function mix(
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number },
  t: number,
) {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

/** Normalize to #rrggbb for inline styles (falls back to a sensible purple). */
export function topicAccentHex(color: string | undefined): string {
  if (!color) return FALLBACK_GRADIENT.from;
  const rgb = parseHex(color);
  if (!rgb) return FALLBACK_GRADIENT.from;
  return color.trim().startsWith("#") ? color.trim() : `#${color.trim()}`;
}

/**
 * Left-to-right gradient: slightly darker than the topic color → slightly lighter,
 * matching the previous purple CTA feel.
 */
export function topicCtaGradientStyle(
  color: string | undefined,
): CSSProperties {
  const rgb = color ? parseHex(color) : null;
  if (!rgb) {
    return {
      backgroundImage: `linear-gradient(to right, ${FALLBACK_GRADIENT.from}, ${FALLBACK_GRADIENT.to})`,
    };
  }
  const from = mix(rgb, BLACK, 0.14);
  const to = mix(rgb, WHITE, 0.2);
  return {
    backgroundImage: `linear-gradient(to right, ${toHex(from.r, from.g, from.b)}, ${toHex(to.r, to.g, to.b)})`,
  };
}
